import React, { useContext, useState } from 'react';
import {
  Segment,
  Form,
  Grid,
  Select,
  DropdownProps,
  Checkbox,
  CheckboxProps,
} from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';

import { Form as FinalForm, Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';

import CalculatorStore from '../../app/stores/calculatorStore';
import { ICalculation } from '../../app/models/calculation';

import formatString from 'format-string-by-pattern';
import ApproximatePrice from './ApproximatePrice';
import Buttons from './Buttons';
import Footer from '../../shared/components/Footer';
import InfoExtra from './InfoExtra';

const CalculatorForm: React.FC = () => {
  const calculatorStore = useContext(CalculatorStore);
  const {
    clearAllFields,
    clearCalculation,
    clearEmissionType,
    clearHaiaCheckbox,
    typesOfCertificates,
    typesOfProperties,
    typesOfCitizenCertificates,
    typesOfEmission,
    hideDate,
    hideTypesOfProperties,
    hideTypesOfCitizenCertificates,
    hideTypesOfEmission,
    hideExternalPrices,
    statesOfBrazil,
    updateCalculation,
    updateDate,
    updateIsHaiaCheckbox,
  } = calculatorStore;

  const dateMask = '99/99/9999';

  const initialCalculationValue: ICalculation = {
    isHaia: false,
    date: '',
    state: '',
    typeOfCertificate: '',
    typeOfCitizenCertificate: '',
    typeOfEmission: '',
    typeOfProperty: '',
  };

  const [calculation, setCalculation] = useState<ICalculation>(initialCalculationValue);

  const clearCalculationAndUpdate = (data: DropdownProps) => {
    clearCalculation();
    updateCalculation(setNewValueForCalculationProperty(data, initialCalculationValue));
  };

  const clearEmissionTypeAndUpdate = (data: DropdownProps) => {
    clearEmissionType();
    clearHaiaCheckbox();
    updateCalculation(setNewValueForCalculationProperty(data, calculation));
  };

  const updateGenericData = (data: DropdownProps) => {
    updateCalculation(setNewValueForCalculationProperty(data, calculation));
  };

  const updateIsHaia = (data: CheckboxProps) => {
    updateIsHaiaCheckbox(data.checked);
  };

  const getYearAndUpdateDate = (date: string) => {
    setCalculation({ ...calculation, date: date });

    if (date.length !== 10) return;
    let tryParseYearFromDate = date.split('/')[2];

    if (tryParseYearFromDate.length === 4) {
      updateDate(tryParseYearFromDate);
    }
  };

  const setNewValueForCalculationProperty = (data: DropdownProps, calculation: ICalculation) => {
    let currentCalculation: ICalculation = { ...calculation };
    let key: keyof ICalculation = data.name.toString();
    currentCalculation[key] = data.value!.toString();
    setCalculation(currentCalculation);
    return { ...currentCalculation };
  };

  const handleClearForm = () => {
    setCalculation(initialCalculationValue);
    clearAllFields();
  };

  return (
    <Grid>
      <Grid.Column width={16}>
        <Segment clearing>
          <FinalForm
            onSubmit={() => {}}
            render={({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Form.Field>
                  <Select
                    value={calculation.typeOfCertificate}
                    onChange={(e, data) => clearCalculationAndUpdate(data)}
                    placeholder="Tipo da certidão"
                    name="typeOfCertificate"
                    options={typesOfCertificates}
                  />
                </Form.Field>

                {hideTypesOfCitizenCertificates ? null : (
                  <Form.Field>
                    <Select
                      value={calculation.typeOfCitizenCertificate}
                      onChange={(e, data) => clearEmissionTypeAndUpdate(data)}
                      placeholder="Tipo da certidão"
                      name="typeOfCitizenCertificate"
                      options={typesOfCitizenCertificates}
                    />
                  </Form.Field>
                )}

                {hideTypesOfEmission ? null : (
                  <Form.Field>
                    <Select
                      value={calculation.typeOfEmission}
                      onChange={(e, data) => updateGenericData(data)}
                      placeholder="Tipo de emissão"
                      name="typeOfEmission"
                      options={typesOfEmission}
                    />
                  </Form.Field>
                )}

                {hideTypesOfProperties ? null : (
                  <Form.Field>
                    <Select
                      value={calculation.typeOfProperty}
                      onChange={(e, data) => updateGenericData(data)}
                      placeholder="Tipo da certidão de imóvel"
                      name="typeOfProperty"
                      options={typesOfProperties}
                    />
                  </Form.Field>
                )}

                <Form.Field>
                  <Select
                    value={calculation.state}
                    onChange={(e, data) => updateGenericData(data)}
                    placeholder="Estado"
                    name="state"
                    options={statesOfBrazil}
                  />
                </Form.Field>

                {hideDate ? null : (
                  <Form.Field>
                    <Field
                      component="input"
                      value={calculation.date}
                      name="date"
                      placeholder="Data da certidão"
                      parse={formatString(dateMask)}
                    />
                    <OnChange name="date">
                      {(value) => {
                        getYearAndUpdateDate(value);
                      }}
                    </OnChange>
                  </Form.Field>
                )}

                {hideExternalPrices ? null : (
                  <Form.Field>
                    <Checkbox
                      label="Apostilamento de Haia"
                      value={calculation.isHaia}
                      onChange={(e, data) => updateIsHaia(data)}
                    />
                  </Form.Field>
                )}

                <ApproximatePrice />
                <Buttons clearForm={handleClearForm} />
                <InfoExtra />
                <Footer />
              </Form>
            )}
          />
        </Segment>
      </Grid.Column>
    </Grid>
  );
};

export default observer(CalculatorForm);
